import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import clsx from "clsx";
import { InlinePdfView } from "@/components/pdf/InlinePdfView";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import {
  Checkbox,
  GermanDecimalInput,
  SelectInput,
  TextInput,
  TextareaInput,
} from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatCurrency } from "@/lib/documentCalc";
import {
  deleteMahnungEntwurf,
  entferneRechnung,
  fetchMahnungPdf,
  fetchMahnungVorschauHtml,
  finalisiereMahnung,
  formatMahnungDate,
  getMahnung,
  ladeEmpfaengerNeu,
  listMahnungEvents,
  mahnungStatusLabel,
  setzeTexteZurueck,
  updateMahnung,
  VERSANDART_OPTIONS,
  versandartLabel,
  type MahnungDetail,
  type MahnungEvent,
  type MahnungKosten,
  type MahnungPatch,
} from "@/lib/mahnwesen";
import { notifyMahnwesenBadgeRefresh } from "@/lib/navBadges";
import { toast } from "@/lib/toast";
import {
  MahnungSendenDialog,
  RechnungHinzufuegenDialog,
  StornoDialog,
  VersandDokumentierenDialog,
} from "@/pages/dokumente/mahnung/MahnungDialogs";
import { MahnungStatusStepper } from "@/pages/dokumente/mahnung/MahnungStatusStepper";
import { MahnungVorschauFrame } from "@/pages/dokumente/mahnung/MahnungVorschauFrame";

// ---------------------------------------------------------------------------
// Formular-Modell
// ---------------------------------------------------------------------------

interface FormState {
  // Empfänger / Adresse
  kunde_name: string;
  kunde_adresszusatz: string;
  kunde_strasse: string;
  kunde_hausnummer: string;
  kunde_plz: string;
  kunde_ort: string;
  ansprechpartner_name: string;
  empfaenger: string;
  empfaenger_cc: string;
  versandart: string;
  // Ersteller (Infoblock „Ansprechpartner/in“)
  created_by_name: string;
  created_by_telefon: string;
  created_by_mobil: string;
  created_by_email: string;
  // Texte
  betreff: string;
  dokument_titel: string;
  anrede: string;
  einleitung: string;
  haupttext: string;
  zahlungsaufforderung: string;
  schlusstext: string;
  hinweistext: string;
  fusstext: string;
  email_betreff: string;
  email_text: string;
  interne_notiz: string;
  // Frist / Zinsen / Kosten
  zahlungsfrist_datum: string;
  zahlungsfrist_box_anzeigen: boolean;
  verzugszinsen_aktiv: boolean;
  zins_modus: string;
  zins_aufschlag: number;
  zins_zinssatz: number;
  zins_festbetrag: number;
  verzugspauschale_aktiv: boolean;
  kosten: Array<{
    typ: string;
    bezeichnung: string;
    betrag: number;
    begruendung: string;
    rechtsgrundlage: string;
    automatisch: boolean;
  }>;
  positionen: Array<{ id: number; verzugsbeginn: string }>;
}

function toForm(m: MahnungDetail): FormState {
  return {
    kunde_name: m.kunde_name || "",
    kunde_adresszusatz: m.kunde_adresszusatz || "",
    kunde_strasse: m.kunde_strasse || "",
    kunde_hausnummer: m.kunde_hausnummer || "",
    kunde_plz: m.kunde_plz || "",
    kunde_ort: m.kunde_ort || "",
    ansprechpartner_name: m.ansprechpartner_name || "",
    empfaenger: m.empfaenger || "",
    empfaenger_cc: m.empfaenger_cc || "",
    versandart: m.versandart || "email",
    created_by_name: m.created_by_name || "",
    created_by_telefon: m.created_by_telefon || "",
    created_by_mobil: m.created_by_mobil || "",
    created_by_email: m.created_by_email || "",
    betreff: m.betreff || "",
    dokument_titel: m.dokument_titel || "",
    anrede: m.anrede || "",
    einleitung: m.einleitung || "",
    haupttext: m.haupttext || "",
    zahlungsaufforderung: m.zahlungsaufforderung || "",
    schlusstext: m.schlusstext || "",
    hinweistext: m.hinweistext || "",
    fusstext: m.fusstext || "",
    email_betreff: m.email_betreff || "",
    email_text: m.email_text || "",
    interne_notiz: m.interne_notiz || "",
    zahlungsfrist_datum: (m.zahlungsfrist_datum || "").slice(0, 10),
    zahlungsfrist_box_anzeigen: Boolean(m.zahlungsfrist_box_anzeigen),
    verzugszinsen_aktiv: Boolean(m.verzugszinsen_aktiv),
    zins_modus: m.zins_modus || "gesetzlich",
    zins_aufschlag: Number(m.zins_aufschlag ?? 0),
    zins_zinssatz: Number(m.zins_zinssatz ?? 0),
    zins_festbetrag: Number(m.zins_festbetrag ?? 0),
    verzugspauschale_aktiv: Boolean(m.verzugspauschale_aktiv),
    kosten: (m.kosten || [])
      .filter((k) => k.typ !== "verzugszinsen")
      .map((k: MahnungKosten) => ({
        typ: k.typ,
        bezeichnung: k.bezeichnung,
        betrag: k.betrag,
        begruendung: k.begruendung || "",
        rechtsgrundlage: k.rechtsgrundlage || "",
        automatisch: k.automatisch,
      })),
    positionen: (m.positionen || []).map((p) => ({
      id: p.id,
      verzugsbeginn: (p.verzugsbeginn || "").slice(0, 10),
    })),
  };
}

function toPatch(form: FormState): MahnungPatch {
  return {
    kunde_name: form.kunde_name,
    kunde_adresszusatz: form.kunde_adresszusatz,
    kunde_strasse: form.kunde_strasse,
    kunde_hausnummer: form.kunde_hausnummer,
    kunde_plz: form.kunde_plz,
    kunde_ort: form.kunde_ort,
    ansprechpartner_name: form.ansprechpartner_name,
    empfaenger: form.empfaenger,
    empfaenger_cc: form.empfaenger_cc,
    versandart: form.versandart,
    created_by_name: form.created_by_name,
    created_by_telefon: form.created_by_telefon,
    created_by_mobil: form.created_by_mobil,
    created_by_email: form.created_by_email,
    betreff: form.betreff,
    dokument_titel: form.dokument_titel,
    anrede: form.anrede,
    einleitung: form.einleitung,
    haupttext: form.haupttext,
    zahlungsaufforderung: form.zahlungsaufforderung,
    schlusstext: form.schlusstext,
    hinweistext: form.hinweistext,
    fusstext: form.fusstext,
    email_betreff: form.email_betreff,
    email_text: form.email_text,
    interne_notiz: form.interne_notiz,
    zahlungsfrist_datum: form.zahlungsfrist_datum || undefined,
    zahlungsfrist_box_anzeigen: form.zahlungsfrist_box_anzeigen,
    verzugszinsen_aktiv: form.verzugszinsen_aktiv,
    zins_modus: form.zins_modus,
    zins_aufschlag: form.zins_aufschlag,
    zins_zinssatz: form.zins_zinssatz,
    zins_festbetrag: form.zins_festbetrag,
    verzugspauschale_aktiv: form.verzugspauschale_aktiv,
    kosten: form.kosten.map((k) => ({
      typ: k.typ,
      bezeichnung: k.bezeichnung,
      betrag: k.betrag,
      begruendung: k.begruendung || undefined,
      rechtsgrundlage: k.rechtsgrundlage || undefined,
      automatisch: k.automatisch,
    })),
    positionen: form.positionen.map((p) => ({ id: p.id, verzugsbeginn: p.verzugsbeginn || null })),
  };
}

const AUTOSAVE_DELAY_MS = 900;

// ---------------------------------------------------------------------------
// Kleine Bausteine
// ---------------------------------------------------------------------------

function EditorSection({
  nummer,
  title,
  description,
  summary,
  actions,
  defaultOpen = true,
  children,
}: {
  nummer: number;
  title: string;
  description?: ReactNode;
  summary?: ReactNode;
  actions?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-ios-lg bg-card shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50">
      <div className="flex items-start gap-3 px-4 py-3">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-start gap-3 text-left"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-fill-tertiary text-xs font-bold text-label-secondary">
            {nummer}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-label">{title}</span>
            {description ? <span className="block text-xs text-label-secondary">{description}</span> : null}
            {!open && summary ? <span className="mt-1 block text-xs text-label-tertiary">{summary}</span> : null}
          </span>
          <span className="mt-0.5 text-label-tertiary" aria-hidden>
            {open ? "−" : "+"}
          </span>
        </button>
        {actions ? <div className="flex shrink-0 flex-wrap items-center gap-1.5">{actions}</div> : null}
      </div>
      {open ? <div className="space-y-3 border-t border-separator px-4 py-3">{children}</div> : null}
    </section>
  );
}

function LinkButton({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex h-8 items-center rounded-lg px-3 text-sm font-medium text-accent hover:bg-fill-tertiary"
    >
      {children}
    </Link>
  );
}

function StatusPill({ status }: { status: string }) {
  const cls =
    status === "entwurf"
      ? "bg-fill-tertiary text-label-secondary"
      : status === "versendet"
        ? "bg-status-green/15 text-status-green"
        : status === "storniert" || status === "fehlgeschlagen"
          ? "bg-status-red/15 text-status-red"
          : "bg-status-orange/15 text-status-orange";
  return (
    <span className={clsx("rounded-full px-2.5 py-1 text-xs font-semibold", cls)}>{mahnungStatusLabel(status)}</span>
  );
}

// ---------------------------------------------------------------------------
// Seite
// ---------------------------------------------------------------------------

export function MahnungEditorPage() {
  const { id } = useParams<{ id: string }>();
  const mahnungId = Number(id);
  const navigate = useNavigate();

  const [detail, setDetail] = useState<MahnungDetail | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [baseline, setBaseline] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [previewSeite, setPreviewSeite] = useState<"erste" | "folge">("erste");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const [zinsDialogOpen, setZinsDialogOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [events, setEvents] = useState<MahnungEvent[] | null>(null);
  const [mailOpen, setMailOpen] = useState(false);
  const [versandOpen, setVersandOpen] = useState(false);
  const [stornoOpen, setStornoOpen] = useState(false);
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [rechnungDialogOpen, setRechnungDialogOpen] = useState(false);

  const status = detail?.status || "entwurf";
  const istEntwurf = status === "entwurf";
  const kannBearbeiten = istEntwurf && Boolean(detail?.permissions?.bearbeiten);
  const dirty = form != null && JSON.stringify(form) !== baseline;

  const applyDetail = useCallback((m: MahnungDetail) => {
    setDetail(m);
    const f = toForm(m);
    setForm(f);
    setBaseline(JSON.stringify(f));
    setPreviewKey((k) => k + 1);
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      applyDetail(await getMahnung(mahnungId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Mahnung konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [mahnungId, applyDetail]);

  useEffect(() => {
    if (Number.isFinite(mahnungId)) void reload();
  }, [mahnungId, reload]);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  // --- Vorschau: Entwurf = Live-HTML, sonst archiviertes PDF ----------------------
  useEffect(() => {
    if (!detail || !Number.isFinite(mahnungId)) return;
    const controller = new AbortController();
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewBlob(null);

    const load =
      detail.status === "entwurf"
        ? fetchMahnungVorschauHtml(mahnungId, previewSeite, controller.signal).then(
            (html) => new Blob([html], { type: "text/html;charset=utf-8" }),
          )
        : fetchMahnungPdf(mahnungId, controller.signal).then(
            (blob) => new Blob([blob], { type: "application/pdf" }),
          );

    void load
      .then((blob) => {
        if (controller.signal.aborted) return;
        setPreviewBlob(blob);
        if (detail.status !== "entwurf") {
          if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
          previewUrlRef.current = null;
          setPreviewUrl(null);
          return;
        }
        const next = URL.createObjectURL(blob);
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = next;
        setPreviewUrl(next);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
          previewUrlRef.current = null;
        }
        setPreviewUrl(null);
        setPreviewBlob(null);
        setPreviewError(err instanceof Error ? err.message : "Vorschau konnte nicht geladen werden.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setPreviewLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [detail, mahnungId, previewKey, previewSeite]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  // --- Speichern (automatisch, verzögert) -----------------------------------------
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!form || !detail || detail.status !== "entwurf") return true;
    const snapshot = JSON.stringify(form);
    setSaving(true);
    setSaveError(null);
    try {
      const next = await updateMahnung(mahnungId, toPatch(form));
      const nextForm = toForm(next);
      setDetail(next);
      setBaseline(JSON.stringify(nextForm));
      // Nur übernehmen, wenn der Nutzer zwischenzeitlich nichts weiter getippt hat –
      // sonst bleibt der Stand „dirty“ und wird beim nächsten Zyklus gespeichert.
      setForm((prev) => (prev && JSON.stringify(prev) === snapshot ? nextForm : prev));
      setLastSavedAt(new Date());
      setPreviewKey((k) => k + 1);
      return true;
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
      return false;
    } finally {
      setSaving(false);
    }
  }, [form, detail, mahnungId]);

  useEffect(() => {
    if (!kannBearbeiten || !dirty || saving || saveError) return;
    const timer = window.setTimeout(() => void handleSave(), AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [kannBearbeiten, dirty, saving, saveError, handleSave]);

  /** Vor Aktionen, die den gespeicherten Stand brauchen: offene Änderungen wegschreiben. */
  async function flushSave(): Promise<boolean> {
    if (!dirty) return true;
    return handleSave();
  }

  // --- Aktionen -------------------------------------------------------------------
  async function handleFinalisieren(force = false) {
    if (!detail) return;
    setBusy("finalisieren");
    try {
      if (!(await flushSave())) return;
      applyDetail(await finalisiereMahnung(mahnungId, force));
      toast.success("Finalisiert – Nummer vergeben und PDF archiviert. Jetzt versenden.");
      notifyMahnwesenBadgeRefresh();
      setForceDialogOpen(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setForceDialogOpen(true);
      } else {
        toast.error(err instanceof ApiError ? err.message : "Finalisierung fehlgeschlagen.");
      }
    } finally {
      setBusy(null);
    }
  }

  async function handlePdf() {
    setBusy("pdf");
    try {
      if (istEntwurf && !(await flushSave())) return;
      const blob = await fetchMahnungPdf(mahnungId);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "PDF konnte nicht geladen werden.");
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete() {
    if (!window.confirm("Diesen Entwurf wirklich löschen? Die Rechnungen bleiben unverändert im Mahnlauf.")) return;
    setBusy("loeschen");
    try {
      await deleteMahnungEntwurf(mahnungId);
      toast.success("Entwurf gelöscht.");
      notifyMahnwesenBadgeRefresh();
      navigate("/dokumente/mahnwesen");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  }

  async function handleEmpfaengerNeuLaden() {
    setBusy("empfaenger");
    try {
      if (!(await flushSave())) return;
      applyDetail(await ladeEmpfaengerNeu(mahnungId));
      toast.success("Empfänger aus den Kundenstammdaten übernommen.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Empfänger konnte nicht ermittelt werden.");
    } finally {
      setBusy(null);
    }
  }

  async function handleTexteZuruecksetzen() {
    if (
      !window.confirm(
        "Alle Dokument- und E-Mail-Texte dieses Entwurfs durch die Standardtexte der Mahnstufe ersetzen? Eigene Änderungen gehen verloren.",
      )
    )
      return;
    setBusy("texte");
    try {
      if (!(await flushSave())) return;
      applyDetail(await setzeTexteZurueck(mahnungId));
      toast.success("Texte aus der Mahnstufe übernommen.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Texte konnten nicht zurückgesetzt werden.");
    } finally {
      setBusy(null);
    }
  }

  async function handleRechnungEntfernen(positionId: number, nummer: string) {
    if (!window.confirm(`Rechnung ${nummer} aus dieser Mahnung entfernen?`)) return;
    setBusy(`pos-${positionId}`);
    try {
      if (!(await flushSave())) return;
      applyDetail(await entferneRechnung(mahnungId, positionId));
      toast.success("Rechnung entfernt.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Rechnung konnte nicht entfernt werden.");
    } finally {
      setBusy(null);
    }
  }

  async function openEvents() {
    setEventsOpen(true);
    try {
      setEvents(await listMahnungEvents(mahnungId));
    } catch {
      setEvents([]);
    }
  }

  const fehler = useMemo(() => (detail?.warnungen || []).filter((w) => w.typ === "fehler"), [detail]);
  const warnungen = useMemo(() => (detail?.warnungen || []).filter((w) => w.typ !== "fehler"), [detail]);
  const textHinweise = detail?.text_hinweise || [];

  if (!Number.isFinite(mahnungId)) {
    return (
      <AppShell title="Mahnung">
        <Alert variant="error">Ungültige Mahnungs-ID.</Alert>
      </AppShell>
    );
  }

  const titel = detail
    ? `${detail.stufe_bezeichnung}${detail.nummer ? ` ${detail.nummer}` : " – Entwurf"}`
    : "Mahnung";
  const stammdatenLink = detail?.geschaeftspartner_id
    ? `/kontakte/kunden/${detail.geschaeftspartner_id}?tab=rechnung`
    : null;
  const stufeLink = detail?.stufe_id
    ? `/dokumente/mahnwesen?tab=einstellungen&stufe=${detail.stufe_id}`
    : "/dokumente/mahnwesen?tab=einstellungen";
  const layoutLink = "/dokumente/layout?typ=mahnung";
  const perEmail = (form?.versandart || detail?.versandart || "email") === "email";

  // Speicherstatus für die Kopfzeile
  let saveStatus: ReactNode = null;
  if (kannBearbeiten) {
    if (saveError) {
      saveStatus = (
        <span className="flex items-center gap-2 text-xs text-status-red">
          {saveError}
          <Button type="button" size="sm" variant="secondary" onClick={() => void handleSave()}>
            Erneut speichern
          </Button>
        </span>
      );
    } else if (saving) {
      saveStatus = (
        <span className="flex items-center gap-1.5 text-xs text-label-secondary">
          <Spinner /> Wird gespeichert …
        </span>
      );
    } else if (dirty) {
      saveStatus = <span className="text-xs text-label-secondary">Änderungen werden gleich gespeichert …</span>;
    } else if (lastSavedAt) {
      saveStatus = (
        <span className="text-xs text-status-green">
          Gespeichert {lastSavedAt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
        </span>
      );
    } else {
      saveStatus = <span className="text-xs text-label-tertiary">Änderungen werden automatisch gespeichert</span>;
    }
  }

  return (
    <AppShell
      title={titel}
      actions={
        <Link
          to="/dokumente/mahnwesen"
          className="inline-flex items-center rounded-lg border border-[var(--eh-color-border)] px-3 py-2 text-sm font-medium text-label hover:bg-[var(--eh-color-surface-muted)]"
        >
          Zurück zum Mahnwesen
        </Link>
      }
    >
      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-label-secondary">
          <Spinner /> Wird geladen …
        </div>
      ) : error ? (
        <Alert variant="error">{error}</Alert>
      ) : detail && form ? (
        <div className="space-y-4">
          {/* Ablauf + Kopfzeile */}
          <section className="space-y-3 rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50">
            <MahnungStatusStepper status={status} />
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-separator pt-3">
              <div className="flex min-w-0 flex-wrap items-center gap-3">
                <StatusPill status={status} />
                <div className="text-sm text-label-secondary">
                  <span className="font-medium text-label">{detail.kunde_name || "—"}</span>
                  {detail.kundenart ? ` · ${detail.kundenart === "unternehmen" ? "Unternehmen" : "Verbraucher"}` : ""}
                  {" · "}
                  {detail.positionen.length} {detail.positionen.length === 1 ? "Rechnung" : "Rechnungen"}
                  {" · "}
                  <span className="font-semibold tabular-nums text-label">
                    Gesamt {formatCurrency(detail.summe_gesamt, "EUR")}
                  </span>
                </div>
                {saveStatus}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" size="sm" variant="ghost" onClick={() => void openEvents()}>
                  Verlauf
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  loading={busy === "pdf"}
                  onClick={() => void handlePdf()}
                >
                  {istEntwurf ? "Entwurfs-PDF" : "PDF öffnen"}
                </Button>

                {istEntwurf ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={busy != null || !detail.permissions?.bearbeiten}
                      onClick={() => void handleDelete()}
                    >
                      Entwurf löschen
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      loading={busy === "finalisieren"}
                      disabled={!detail.permissions?.finalisieren || saving || Boolean(saveError)}
                      onClick={() => void handleFinalisieren(false)}
                    >
                      Finalisieren
                    </Button>
                  </>
                ) : null}

                {status === "erstellt" || status === "fehlgeschlagen" ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant={perEmail ? "secondary" : "primary"}
                      disabled={!detail.permissions?.versenden}
                      onClick={() => setVersandOpen(true)}
                    >
                      {perEmail ? "Anders versendet …" : "Versand dokumentieren"}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={perEmail ? "primary" : "secondary"}
                      disabled={!detail.permissions?.versenden}
                      onClick={() => setMailOpen(true)}
                    >
                      {status === "fehlgeschlagen" ? "Erneut per E-Mail senden" : "Per E-Mail senden"}
                    </Button>
                  </>
                ) : null}

                {status === "versendet" ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={!detail.permissions?.versenden}
                    onClick={() => setMailOpen(true)}
                  >
                    Erneut per E-Mail senden
                  </Button>
                ) : null}

                {status !== "storniert" && status !== "entwurf" ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    disabled={!detail.permissions?.stornieren}
                    onClick={() => setStornoOpen(true)}
                  >
                    Stornieren
                  </Button>
                ) : null}
              </div>
            </div>

            <NaechsterSchritt detail={detail} perEmail={perEmail} />
          </section>

          {status === "storniert" ? (
            <Alert variant="error" title="Storniert">
              {detail.storno_grund || "Diese Mahnung wurde storniert."}
              {detail.storniert_am ? ` (${formatMahnungDate(detail.storniert_am)})` : ""}
            </Alert>
          ) : null}

          {fehler.length ? (
            <Alert variant="error" title="Das muss vor dem Finalisieren behoben werden">
              <ul className="list-disc space-y-1 pl-4">
                {fehler.map((w, i) => (
                  <li key={i}>{w.text}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
          {warnungen.length ? (
            <Alert variant="warning" title="Bitte prüfen">
              <ul className="list-disc space-y-1 pl-4">
                {warnungen.map((w, i) => (
                  <li key={i}>{w.text}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
          {detail.basiszins && !detail.basiszins.aktuell_gueltig && form.verzugszinsen_aktiv ? (
            <Alert variant="warning">
              Für den aktuellen Zeitraum ist kein Basiszinssatz gepflegt – Zinsen werden nur bis zum letzten
              gepflegten Zeitraum berechnet.{" "}
              <Link to="/dokumente/mahnwesen?tab=einstellungen" className="font-medium underline">
                In den Einstellungen ergänzen
              </Link>
            </Alert>
          ) : null}

          <div className="grid gap-4 xl:grid-cols-[minmax(360px,520px)_1fr]">
            {/* Linke Spalte: Formular */}
            <div className="space-y-3">
              {!istEntwurf ? (
                <Alert variant="info">
                  Diese Mahnung ist finalisiert; Inhalt und PDF sind eingefroren. Für Änderungen die Mahnung
                  stornieren und einen neuen Entwurf erstellen.
                </Alert>
              ) : !detail.permissions?.bearbeiten ? (
                <Alert variant="info">Sie haben keine Berechtigung, Entwürfe zu bearbeiten.</Alert>
              ) : null}

              {/* 1 · Empfänger */}
              <EditorSection
                nummer={1}
                title="Empfänger"
                description="Anschrift auf dem Dokument und E-Mail-Adresse für den Versand."
                summary={`${form.kunde_name || "—"} · ${form.empfaenger || "keine E-Mail"}`}
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <TextInput
                    label="Firma / Name"
                    value={form.kunde_name}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("kunde_name", e.target.value)}
                  />
                  <TextInput
                    label="Adresszusatz"
                    value={form.kunde_adresszusatz}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("kunde_adresszusatz", e.target.value)}
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-[1fr_110px]">
                  <TextInput
                    label="Straße"
                    value={form.kunde_strasse}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("kunde_strasse", e.target.value)}
                  />
                  <TextInput
                    label="Nr."
                    value={form.kunde_hausnummer}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("kunde_hausnummer", e.target.value)}
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-[120px_1fr]">
                  <TextInput
                    label="PLZ"
                    value={form.kunde_plz}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("kunde_plz", e.target.value)}
                  />
                  <TextInput
                    label="Ort"
                    value={form.kunde_ort}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("kunde_ort", e.target.value)}
                  />
                </div>
                <TextInput
                  label="Ansprechpartner beim Kunden (z. Hd.)"
                  value={form.ansprechpartner_name}
                  disabled={!kannBearbeiten}
                  onChange={(e) => set("ansprechpartner_name", e.target.value)}
                  hint="Wird in der Anschrift ausgegeben und für {{Ansprechpartner.briefanrede}} verwendet."
                />

                <div className="space-y-3 rounded-lg border border-separator/70 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-medium text-label">E-Mail-Versand</div>
                    <div className="flex flex-wrap gap-1.5">
                      {kannBearbeiten ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          loading={busy === "empfaenger"}
                          onClick={() => void handleEmpfaengerNeuLaden()}
                        >
                          Aus Stammdaten übernehmen
                        </Button>
                      ) : null}
                      {stammdatenLink ? <LinkButton to={stammdatenLink}>Stammdaten öffnen</LinkButton> : null}
                    </div>
                  </div>
                  <TextInput
                    label="An"
                    value={form.empfaenger}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("empfaenger", e.target.value)}
                    placeholder="buchhaltung@kunde.de"
                    hint={
                      detail.empfaenger_quelle_label
                        ? `Herkunft: ${detail.empfaenger_quelle_label}. Mehrere Adressen mit Semikolon trennen.`
                        : "Mehrere Adressen mit Semikolon trennen."
                    }
                  />
                  <TextInput
                    label="CC"
                    value={form.empfaenger_cc}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("empfaenger_cc", e.target.value)}
                    hint={
                      detail.team_cc
                        ? `Zusätzlich gehen Team-Kopien an: ${detail.team_cc}`
                        : "Optional, z. B. der Projekt-Ansprechpartner."
                    }
                  />
                  <SelectInput
                    label="Geplanter Versandweg"
                    value={form.versandart}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("versandart", e.target.value)}
                    hint={VERSANDART_OPTIONS.find((o) => o.value === form.versandart)?.hinweis}
                  >
                    {VERSANDART_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </SelectInput>
                  <p className="text-xs text-label-tertiary">
                    Welche E-Mail für Zahlungserinnerung und Mahnungen gilt, wird in den Kundenstammdaten unter
                    „Rechnung &amp; Mahnwesen“ festgelegt – die Adresse des Ansprechpartners ist nur der Notnagel.
                  </p>
                </div>
              </EditorSection>

              {/* 2 · Forderungen */}
              <EditorSection
                nummer={2}
                title="Rechnungen"
                description="Welche offenen Rechnungen gemahnt werden."
                summary={`${detail.positionen.length} Rechnung(en) · ${formatCurrency(detail.summe_hauptforderung, "EUR")} offen`}
                actions={
                  kannBearbeiten ? (
                    <Button type="button" size="sm" variant="secondary" onClick={() => setRechnungDialogOpen(true)}>
                      Rechnung hinzufügen
                    </Button>
                  ) : null
                }
              >
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-xs">
                    <thead className="text-label-secondary">
                      <tr>
                        <th className="py-1 pr-2 font-semibold">Rechnung</th>
                        <th className="py-1 pr-2 font-semibold">Fällig</th>
                        <th className="py-1 pr-2 text-right font-semibold">Offen</th>
                        <th className="py-1 pr-2 text-right font-semibold">Zinsen</th>
                        <th className="py-1 pr-2 font-semibold">Verzug seit</th>
                        {kannBearbeiten ? <th className="py-1" /> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {detail.positionen.map((pos) => {
                        const formPos = form.positionen.find((p) => p.id === pos.id);
                        const nummer = pos.rechnung_nummer || `#${pos.dokument_id}`;
                        return (
                          <tr key={pos.id} className="border-t border-separator/60">
                            <td className="py-1.5 pr-2">
                              <div className="font-medium">{nummer}</div>
                              <div className="text-[10px] text-label-tertiary">vom {formatMahnungDate(pos.rechnung_datum)}</div>
                            </td>
                            <td className="py-1.5 pr-2 tabular-nums">{formatMahnungDate(pos.faelligkeitsdatum)}</td>
                            <td className="py-1.5 pr-2 text-right tabular-nums">
                              {formatCurrency(pos.betrag_offen, "EUR")}
                              {pos.betrag_bezahlt > 0 ? (
                                <div className="text-[10px] text-label-tertiary">
                                  bezahlt {formatCurrency(pos.betrag_bezahlt, "EUR")}
                                </div>
                              ) : null}
                            </td>
                            <td className="py-1.5 pr-2 text-right tabular-nums">{formatCurrency(pos.zinsen, "EUR")}</td>
                            <td className="py-1.5 pr-2">
                              {kannBearbeiten && detail.permissions?.verzugsbeginn_ueberschreiben ? (
                                <input
                                  type="date"
                                  className="eh-input !py-1 text-xs"
                                  value={formPos?.verzugsbeginn || ""}
                                  onChange={(e) =>
                                    set(
                                      "positionen",
                                      form.positionen.map((p) =>
                                        p.id === pos.id ? { ...p, verzugsbeginn: e.target.value } : p,
                                      ),
                                    )
                                  }
                                />
                              ) : (
                                <span className="tabular-nums">{formatMahnungDate(pos.verzugsbeginn)}</span>
                              )}
                              {pos.verzugsbeginn_grund_label ? (
                                <div className="text-[10px] text-label-tertiary">{pos.verzugsbeginn_grund_label}</div>
                              ) : null}
                            </td>
                            {kannBearbeiten ? (
                              <td className="py-1.5 text-right">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  disabled={detail.positionen.length <= 1}
                                  loading={busy === `pos-${pos.id}`}
                                  title={
                                    detail.positionen.length <= 1
                                      ? "Die letzte Rechnung kann nicht entfernt werden – stattdessen den Entwurf löschen."
                                      : "Rechnung aus dieser Mahnung entfernen"
                                  }
                                  onClick={() => void handleRechnungEntfernen(pos.id, nummer)}
                                >
                                  Entfernen
                                </Button>
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between border-t border-separator pt-2 text-sm">
                  <span className="text-label-secondary">Hauptforderung</span>
                  <span className="font-medium tabular-nums">{formatCurrency(detail.summe_hauptforderung, "EUR")}</span>
                </div>
              </EditorSection>

              {/* 3 · Zinsen & Kosten */}
              <EditorSection
                nummer={3}
                title="Verzugszinsen & Mahnkosten"
                description="Nebenforderungen, die zusätzlich zur Hauptforderung ausgewiesen werden."
                summary={`Zinsen ${formatCurrency(detail.summe_zinsen, "EUR")} · Kosten ${formatCurrency(detail.summe_kosten, "EUR")}`}
                defaultOpen={false}
              >
                <Checkbox
                  label="Verzugszinsen berechnen"
                  description="Gesetzlich: Basiszins + 5 Punkte (Verbraucher) bzw. + 9 Punkte (Unternehmen), § 288 BGB."
                  checked={form.verzugszinsen_aktiv}
                  disabled={!kannBearbeiten}
                  onChange={(e) => set("verzugszinsen_aktiv", e.target.checked)}
                />
                {form.verzugszinsen_aktiv ? (
                  <div className="space-y-3 rounded-lg border border-separator/70 p-3">
                    <SelectInput
                      label="Zinsmodus"
                      value={form.zins_modus}
                      disabled={!kannBearbeiten || !detail.permissions?.zins_ueberschreiben}
                      onChange={(e) => set("zins_modus", e.target.value)}
                    >
                      <option value="gesetzlich">Gesetzlich (Basiszins + Aufschlag, § 288 BGB)</option>
                      <option value="eigener_aufschlag">Eigener Aufschlag auf Basiszins</option>
                      <option value="eigener_zinssatz">Fester Zinssatz p. a.</option>
                      <option value="fester_betrag">Fester Zinsbetrag</option>
                    </SelectInput>
                    {form.zins_modus === "eigener_aufschlag" ? (
                      <GermanDecimalInput
                        label="Aufschlag (%-Punkte)"
                        value={form.zins_aufschlag}
                        onValueChange={(v) => set("zins_aufschlag", v)}
                        disabled={!kannBearbeiten}
                      />
                    ) : null}
                    {form.zins_modus === "eigener_zinssatz" ? (
                      <GermanDecimalInput
                        label="Zinssatz p. a. (%)"
                        value={form.zins_zinssatz}
                        onValueChange={(v) => set("zins_zinssatz", v)}
                        disabled={!kannBearbeiten}
                      />
                    ) : null}
                    {form.zins_modus === "fester_betrag" ? (
                      <GermanDecimalInput
                        label="Zinsbetrag (EUR)"
                        value={form.zins_festbetrag}
                        onValueChange={(v) => set("zins_festbetrag", v)}
                        disabled={!kannBearbeiten}
                      />
                    ) : null}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-label-secondary">Zinsen zum Stichtag</span>
                      <span className="font-medium tabular-nums">{formatCurrency(detail.summe_zinsen, "EUR")}</span>
                    </div>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setZinsDialogOpen(true)}>
                      Berechnung im Detail anzeigen
                    </Button>
                  </div>
                ) : null}
                <Checkbox
                  label="40-€-Verzugspauschale (§ 288 Abs. 5 BGB)"
                  description="Nur gegenüber Unternehmen zulässig."
                  checked={form.verzugspauschale_aktiv}
                  disabled={!kannBearbeiten}
                  onChange={(e) => set("verzugspauschale_aktiv", e.target.checked)}
                />

                <div className="border-t border-separator pt-3">
                  <div className="mb-2 text-sm font-medium text-label">Mahnkosten / sonstige Nebenforderungen</div>
                  {form.kosten.length === 0 ? (
                    <p className="text-xs text-label-secondary">Keine Kostenpositionen.</p>
                  ) : (
                    <div className="space-y-2">
                      {form.kosten.map((k, idx) => (
                        <div key={idx} className="rounded-lg border border-separator/70 p-2">
                          <div className="grid grid-cols-[1fr_110px] gap-2">
                            <TextInput
                              label="Bezeichnung"
                              value={k.bezeichnung}
                              disabled={!kannBearbeiten || !detail.permissions?.kosten_aendern}
                              onChange={(e) =>
                                set(
                                  "kosten",
                                  form.kosten.map((x, i) => (i === idx ? { ...x, bezeichnung: e.target.value } : x)),
                                )
                              }
                            />
                            <GermanDecimalInput
                              label="Betrag (EUR)"
                              value={k.betrag}
                              disabled={!kannBearbeiten || !detail.permissions?.kosten_aendern}
                              onValueChange={(v) =>
                                set(
                                  "kosten",
                                  form.kosten.map((x, i) => (i === idx ? { ...x, betrag: v } : x)),
                                )
                              }
                            />
                          </div>
                          <TextInput
                            label="Begründung (erscheint auf dem Dokument)"
                            value={k.begruendung}
                            disabled={!kannBearbeiten || !detail.permissions?.kosten_aendern}
                            onChange={(e) =>
                              set(
                                "kosten",
                                form.kosten.map((x, i) => (i === idx ? { ...x, begruendung: e.target.value } : x)),
                              )
                            }
                            className="mt-2"
                          />
                          <div className="mt-1 flex items-center justify-between">
                            <span className="text-[11px] text-label-tertiary">
                              {k.rechtsgrundlage || (k.automatisch ? "Automatisch aus Mahnstufe" : "Manuell")}
                            </span>
                            {kannBearbeiten && detail.permissions?.kosten_aendern ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  set(
                                    "kosten",
                                    form.kosten.filter((_, i) => i !== idx),
                                  )
                                }
                              >
                                Entfernen
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {kannBearbeiten && detail.permissions?.kosten_aendern ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="mt-2"
                      onClick={() =>
                        set("kosten", [
                          ...form.kosten,
                          {
                            typ: "manuell",
                            bezeichnung: "Mahnkosten",
                            betrag: 0,
                            begruendung: "",
                            rechtsgrundlage: "",
                            automatisch: false,
                          },
                        ])
                      }
                    >
                      Kostenposition hinzufügen
                    </Button>
                  ) : null}
                  <div className="mt-2 flex items-center justify-between border-t border-separator pt-2 text-sm">
                    <span className="text-label-secondary">Kosten gesamt</span>
                    <span className="font-medium tabular-nums">{formatCurrency(detail.summe_kosten, "EUR")}</span>
                  </div>
                </div>
              </EditorSection>

              {/* 4 · Zahlungsfrist */}
              <EditorSection
                nummer={4}
                title="Zahlungsfrist"
                description="Bis wann der Kunde zahlen soll. Der Termin steht als {{Mahnung.zahlungsfrist}} in den Texten."
                summary={`Zahlbar bis ${formatMahnungDate(form.zahlungsfrist_datum)}${form.zahlungsfrist_box_anzeigen ? " · mit Hinweiskasten" : ""}`}
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <TextInput
                    label="Zahlbar bis"
                    type="date"
                    value={form.zahlungsfrist_datum}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("zahlungsfrist_datum", e.target.value)}
                    hint={
                      detail.zahlungsfrist_tage != null
                        ? `Vorgabe der Mahnstufe: ${detail.zahlungsfrist_tage} Tage ab Erstellung.`
                        : undefined
                    }
                  />
                  <Checkbox
                    label="Zusätzlich als hervorgehobenen Kasten drucken"
                    description="Nur sinnvoll, wenn der Termin nicht schon im Text der Zahlungsaufforderung genannt wird – sonst erscheint er doppelt."
                    checked={form.zahlungsfrist_box_anzeigen}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("zahlungsfrist_box_anzeigen", e.target.checked)}
                    className="pt-6"
                  />
                </div>
              </EditorSection>

              {/* 5 · Texte */}
              <EditorSection
                nummer={5}
                title="Texte auf dem Dokument"
                description="Reihenfolge im Brief: Titel → Betreff → Anrede → Einleitung → Forderungstabelle → Haupttext → Zahlungsaufforderung → Schlusstext → Hinweis → Fußtext."
                summary={form.einleitung ? form.einleitung.slice(0, 90) + (form.einleitung.length > 90 ? " …" : "") : "Keine Einleitung"}
                actions={
                  <>
                    {kannBearbeiten ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        loading={busy === "texte"}
                        onClick={() => void handleTexteZuruecksetzen()}
                        title="Alle Texte durch die Standardtexte der Mahnstufe ersetzen"
                      >
                        Aus Mahnstufe neu laden
                      </Button>
                    ) : null}
                    {detail.permissions?.einstellungen ? (
                      <LinkButton to={stufeLink}>Standardtexte der Stufe ändern</LinkButton>
                    ) : null}
                  </>
                }
              >
                {textHinweise.length ? (
                  <Alert variant="info" title="Hinweise zu den Texten">
                    <ul className="list-disc space-y-1 pl-4">
                      {textHinweise.map((h, i) => (
                        <li key={i}>{h}</li>
                      ))}
                    </ul>
                  </Alert>
                ) : null}
                <p className="text-xs text-label-tertiary">
                  Änderungen hier gelten nur für diese Mahnung. Platzhalter wie {"{{Mahnung.gesamtforderung}}"},{" "}
                  {"{{Mahnung.zahlungsfrist}}"}, {"{{Mahnung.rechnungsnummern}}"}, {"{{Mahnung.vorherige}}"} oder{" "}
                  {"{{Ansprechpartner.briefanrede}}"} werden in der Vorschau ersetzt.
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <TextInput
                    label="Dokumenttitel"
                    value={form.dokument_titel}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("dokument_titel", e.target.value)}
                    hint="Fett über dem Brief, z. B. „Zahlungserinnerung“. Leer = Name der Mahnstufe."
                  />
                  <TextInput
                    label="Betreffzeile (optional)"
                    value={form.betreff}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("betreff", e.target.value)}
                    hint="Leer lassen – die Rechnungen stehen bereits in der Tabelle."
                  />
                </div>
                <TextInput
                  label="Anrede"
                  value={form.anrede}
                  disabled={!kannBearbeiten}
                  onChange={(e) => set("anrede", e.target.value)}
                  hint="Wird ausgelassen, wenn die Einleitung selbst schon mit einer Anrede beginnt."
                />
                <TextareaInput
                  label="Einleitung"
                  rows={3}
                  value={form.einleitung}
                  disabled={!kannBearbeiten}
                  onChange={(e) => set("einleitung", e.target.value)}
                  hint="Steht vor der Forderungstabelle."
                />
                <TextareaInput
                  label="Haupttext"
                  rows={4}
                  value={form.haupttext}
                  disabled={!kannBearbeiten}
                  onChange={(e) => set("haupttext", e.target.value)}
                  hint="Steht nach der Tabelle – z. B. der Hinweis auf Verzug oder weitere Schritte."
                />
                <TextareaInput
                  label="Zahlungsaufforderung"
                  rows={2}
                  value={form.zahlungsaufforderung}
                  disabled={!kannBearbeiten}
                  onChange={(e) => set("zahlungsaufforderung", e.target.value)}
                  hint="Ein Satz mit Frist und Konto genügt, z. B. „… bis zum {{Mahnung.zahlungsfrist}} auf unser unten genanntes Konto zu überweisen.“"
                />
                <TextareaInput
                  label="Schlusstext"
                  rows={2}
                  value={form.schlusstext}
                  disabled={!kannBearbeiten}
                  onChange={(e) => set("schlusstext", e.target.value)}
                  hint="Grußformel; der „gegenstandslos“-Satz gehört entweder hierher oder in den Hinweistext – nicht in beide."
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <TextareaInput
                    label="Hinweistext (klein)"
                    rows={2}
                    value={form.hinweistext}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("hinweistext", e.target.value)}
                  />
                  <TextareaInput
                    label="Fußtext"
                    rows={2}
                    value={form.fusstext}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("fusstext", e.target.value)}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2 border-t border-separator pt-2 text-xs text-label-tertiary">
                  Briefpapier, Infoblock und Schrift kommen aus dem Layout:
                  <LinkButton to={layoutLink}>Layout für Mahnungen öffnen</LinkButton>
                </div>
              </EditorSection>

              {/* 6 · E-Mail */}
              <EditorSection
                nummer={6}
                title="E-Mail-Text"
                description="Vorbelegung für den Versand – beim Senden noch einmal anpassbar."
                summary={form.email_betreff || "Kein Betreff"}
                defaultOpen={false}
              >
                <TextInput
                  label="E-Mail-Betreff"
                  value={form.email_betreff}
                  disabled={!kannBearbeiten}
                  onChange={(e) => set("email_betreff", e.target.value)}
                />
                <TextareaInput
                  label="E-Mail-Text"
                  rows={6}
                  value={form.email_text}
                  disabled={!kannBearbeiten}
                  onChange={(e) => set("email_text", e.target.value)}
                  hint="Die Mahnung wird als PDF angehängt; die Signatur wird automatisch ergänzt."
                />
              </EditorSection>

              {/* 7 · Ansprechpartner (Infoblock) */}
              <EditorSection
                nummer={7}
                title="Ihr Ansprechpartner (Infoblock)"
                description="Erscheint rechts oben im Infoblock, sofern im Layout aktiviert."
                summary={`${form.created_by_name || "—"}${form.created_by_telefon ? ` · ${form.created_by_telefon}` : ""}`}
                defaultOpen={false}
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <TextInput
                    label="Name"
                    value={form.created_by_name}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("created_by_name", e.target.value)}
                  />
                  <TextInput
                    label="E-Mail"
                    value={form.created_by_email}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("created_by_email", e.target.value)}
                  />
                  <TextInput
                    label="Telefon"
                    value={form.created_by_telefon}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("created_by_telefon", e.target.value)}
                  />
                  <TextInput
                    label="Mobil"
                    value={form.created_by_mobil}
                    disabled={!kannBearbeiten}
                    onChange={(e) => set("created_by_mobil", e.target.value)}
                  />
                </div>
                <p className="text-xs text-label-tertiary">
                  Vorbelegt aus dem Benutzerverzeichnis der Person, die den Entwurf erstellt hat. Welche Zeilen
                  gedruckt werden (Telefon, Mobil, E-Mail), steuert der Infoblock im{" "}
                  <Link to={layoutLink} className="text-accent underline">
                    Layout
                  </Link>
                  .
                </p>
              </EditorSection>

              {/* 8 · Interne Notiz */}
              <EditorSection
                nummer={8}
                title="Interne Notiz"
                description="Nur für das Team sichtbar, erscheint nicht auf dem Dokument."
                summary={form.interne_notiz ? form.interne_notiz.slice(0, 80) : "Keine Notiz"}
                defaultOpen={false}
              >
                <TextareaInput
                  label="Notiz"
                  rows={3}
                  value={form.interne_notiz}
                  onChange={(e) => set("interne_notiz", e.target.value)}
                  disabled={!kannBearbeiten}
                />
              </EditorSection>
            </div>

            {/* Rechte Spalte: Vorschau */}
            <div className="min-w-0">
              <div className="sticky top-4 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-label">
                    {istEntwurf ? "Vorschau – so sieht der Brief aus" : "Archiviertes PDF"}
                  </h3>
                  <div className="flex items-center gap-2">
                    {istEntwurf && (dirty || saving) ? (
                      <span className="text-xs text-label-tertiary">Vorschau aktualisiert sich nach dem Speichern</span>
                    ) : null}
                    {istEntwurf ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setPreviewSeite((s) => (s === "erste" ? "folge" : "erste"))}
                      >
                        {previewSeite === "erste" ? "Folgeseite zeigen" : "Erste Seite zeigen"}
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div className="relative overflow-hidden rounded-ios-lg bg-fill-tertiary/30 shadow-ios-md ring-1 ring-separator/35">
                  {previewLoading && !previewUrl && !previewBlob ? (
                    <div className="flex h-[80vh] items-center justify-center gap-2 text-sm text-label-secondary">
                      <Spinner />
                      Vorschau wird geladen …
                    </div>
                  ) : null}
                  {previewError && !previewUrl && !previewBlob ? (
                    <div className="flex h-[80vh] flex-col items-center justify-center gap-3 px-6 text-center text-sm text-status-red">
                      <span>{previewError}</span>
                      <Button type="button" size="sm" variant="secondary" onClick={() => setPreviewKey((k) => k + 1)}>
                        Erneut versuchen
                      </Button>
                    </div>
                  ) : null}
                  {previewUrl && istEntwurf ? (
                    <MahnungVorschauFrame src={previewUrl} className="h-[80vh] w-full border-0 bg-white" />
                  ) : previewBlob && !istEntwurf ? (
                    <InlinePdfView
                      blob={previewBlob}
                      title="Archiviertes PDF"
                      className="h-[80vh] w-full bg-white"
                    />
                  ) : null}
                  {previewLoading && (previewUrl || previewBlob) ? (
                    <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center pt-3">
                      <span className="rounded-full bg-card/90 px-2.5 py-1 text-[11px] text-label-secondary shadow-sm ring-1 ring-separator/40">
                        Vorschau wird aktualisiert …
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Zinsberechnung */}
      <Dialog open={zinsDialogOpen} onClose={() => setZinsDialogOpen(false)} title="Zinsberechnung im Detail" size="2xl">
        {detail?.zinsberechnung?.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-xs">
              <thead className="text-label-secondary">
                <tr>
                  <th className="py-1.5 pr-3 font-semibold">Rechnung</th>
                  <th className="py-1.5 pr-3 font-semibold">Zeitraum</th>
                  <th className="py-1.5 pr-3 text-right font-semibold">Tage</th>
                  <th className="py-1.5 pr-3 text-right font-semibold">Grundlage</th>
                  <th className="py-1.5 pr-3 text-right font-semibold">Basiszins</th>
                  <th className="py-1.5 pr-3 text-right font-semibold">Zinssatz</th>
                  <th className="py-1.5 text-right font-semibold">Zinsen</th>
                </tr>
              </thead>
              <tbody>
                {detail.zinsberechnung.map((z, i) => (
                  <tr key={i} className="border-t border-separator/60">
                    <td className="py-1.5 pr-3">
                      {z.rechnung_nummer || "—"}
                      {z.verzugsbeginn_grund ? (
                        <div className="text-[10px] text-label-tertiary">{z.verzugsbeginn_grund}</div>
                      ) : null}
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums">
                      {formatMahnungDate(z.von)} – {formatMahnungDate(z.bis)}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {z.tage} / {z.jahr_tage}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{formatCurrency(z.grundlage_betrag, "EUR")}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {z.basiszinssatz != null ? `${String(z.basiszinssatz).replace(".", ",")} %` : "—"}
                      {z.aufschlag != null ? ` + ${String(z.aufschlag).replace(".", ",")}` : ""}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{String(z.zinssatz).replace(".", ",")} %</td>
                    <td className="py-1.5 text-right font-medium tabular-nums">{formatCurrency(z.zinsbetrag, "EUR")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-[11px] text-label-tertiary">
              Formel je Zeitraum: Grundlage × Zinssatz × Zinstage ÷ Jahrestage (act/act, keine Zinseszinsen, § 289
              BGB). Teilzahlungen reduzieren die Grundlage ab dem Folgetag.
            </p>
          </div>
        ) : (
          <p className="text-sm text-label-secondary">Keine Zinsperioden berechnet.</p>
        )}
      </Dialog>

      {/* Verlauf */}
      <Dialog open={eventsOpen} onClose={() => setEventsOpen(false)} title="Verlauf" size="lg">
        {events == null ? (
          <div className="flex items-center gap-2 text-sm text-label-secondary">
            <Spinner /> Laden …
          </div>
        ) : events.length === 0 ? (
          <p className="text-sm text-label-secondary">Keine Ereignisse.</p>
        ) : (
          <ul className="space-y-2">
            {events.map((ev) => (
              <li key={ev.id} className="rounded-lg border border-separator/60 px-3 py-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-label">{ev.typ}</span>
                  <span className="text-label-tertiary">
                    {ev.created_at ? new Date(ev.created_at).toLocaleString("de-DE") : ""}
                  </span>
                </div>
                {ev.nachricht ? <div className="mt-0.5 text-label-secondary">{ev.nachricht}</div> : null}
                {ev.user_name || ev.user_login ? (
                  <div className="mt-0.5 text-label-tertiary">{ev.user_name || ev.user_login}</div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Dialog>

      {/* Finalisieren trotz Warnungen */}
      <Dialog
        open={forceDialogOpen}
        onClose={() => setForceDialogOpen(false)}
        title="Trotz Hinweisen finalisieren?"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setForceDialogOpen(false)}>
              Zurück zum Entwurf
            </Button>
            <Button
              type="button"
              variant="warning"
              loading={busy === "finalisieren"}
              onClick={() => void handleFinalisieren(true)}
            >
              Trotzdem finalisieren
            </Button>
          </div>
        }
      >
        <div className="space-y-2 text-sm">
          <p>Es liegen Hinweise vor. Nach der Finalisierung kann die Mahnung nicht mehr geändert werden.</p>
          <ul className="list-disc space-y-1 pl-5">
            {(detail?.warnungen || []).map((w, i) => (
              <li key={i}>{w.text}</li>
            ))}
          </ul>
        </div>
      </Dialog>

      {detail ? (
        <>
          <MahnungSendenDialog
            open={mailOpen}
            detail={detail}
            onClose={() => setMailOpen(false)}
            onSent={async () => {
              setMailOpen(false);
              toast.success("Mahnung per E-Mail versendet.");
              notifyMahnwesenBadgeRefresh();
              await reload();
            }}
          />
          <VersandDokumentierenDialog
            open={versandOpen}
            detail={detail}
            onClose={() => setVersandOpen(false)}
            onDone={async () => {
              setVersandOpen(false);
              toast.success("Versand dokumentiert – die Mahnung gilt als zugestellt.");
              notifyMahnwesenBadgeRefresh();
              await reload();
            }}
          />
          <StornoDialog
            open={stornoOpen}
            mahnungId={mahnungId}
            onClose={() => setStornoOpen(false)}
            onDone={async () => {
              setStornoOpen(false);
              toast.success("Mahnung storniert.");
              notifyMahnwesenBadgeRefresh();
              await reload();
            }}
          />
          <RechnungHinzufuegenDialog
            open={rechnungDialogOpen}
            detail={detail}
            onClose={() => setRechnungDialogOpen(false)}
            onAdded={(next) => {
              setRechnungDialogOpen(false);
              applyDetail(next);
            }}
          />
        </>
      ) : null}
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// „Was ist jetzt zu tun?“ – kurze Anleitung je Status
// ---------------------------------------------------------------------------

function NaechsterSchritt({ detail, perEmail }: { detail: MahnungDetail; perEmail: boolean }) {
  let text: ReactNode;
  switch (detail.status) {
    case "entwurf":
      text = (
        <>
          <strong className="text-label">Jetzt:</strong> Empfänger, Rechnungen und Texte prüfen – rechts sehen Sie
          den Brief so, wie er verschickt wird. Änderungen werden automatisch gespeichert. Passt alles, auf{" "}
          <strong className="text-label">„Finalisieren“</strong> klicken: Die Mahnung erhält ihre Nummer, das PDF wird
          ohne Entwurfs-Wasserzeichen archiviert und kann danach nicht mehr geändert werden.
        </>
      );
      break;
    case "erstellt":
      text = perEmail ? (
        <>
          <strong className="text-label">Jetzt:</strong> Die Mahnung ist finalisiert, aber noch nicht beim Kunden.
          Über <strong className="text-label">„Per E-Mail senden“</strong> geht sie mit PDF-Anhang raus – Empfänger
          und Text lassen sich dort noch anpassen. Wurde sie stattdessen gedruckt und per Post verschickt, den Versand
          über „Anders versendet …“ dokumentieren.
        </>
      ) : (
        <>
          <strong className="text-label">Jetzt:</strong> Die Mahnung ist finalisiert. Geplanter Versandweg ist{" "}
          <strong className="text-label">{versandartLabel(detail.versandart)}</strong>: PDF öffnen, drucken und
          anschließend über <strong className="text-label">„Versand dokumentieren“</strong> als versendet markieren.
          Alternativ kann sie auch per E-Mail gesendet werden.
        </>
      );
      break;
    case "fehlgeschlagen":
      text = (
        <>
          <strong className="text-status-red">Der E-Mail-Versand ist fehlgeschlagen.</strong> Empfänger-Adresse prüfen
          und erneut senden – oder den Versand auf anderem Weg (Post) dokumentieren. Details stehen im Verlauf.
        </>
      );
      break;
    case "versendet":
      text = (
        <>
          <strong className="text-label">Erledigt.</strong> Versendet am {formatMahnungDate(detail.versendet_am)}
          {detail.versendet_via ? ` per ${versandartLabel(detail.versendet_via)}` : ""}. Zahlungseingänge werden an der
          Rechnung erfasst. Bleibt die Zahlung nach der Frist ({formatMahnungDate(detail.zahlungsfrist_datum)}) aus,
          erscheint die Rechnung im Mahnlauf automatisch für die nächste Stufe.
        </>
      );
      break;
    default:
      text = (
        <>
          Diese Mahnung ist storniert und hat keine Wirkung mehr. Die Rechnung kann im Mahnlauf erneut gemahnt werden.
        </>
      );
  }
  return <p className="rounded-lg bg-fill-tertiary/40 px-3 py-2 text-sm text-label-secondary">{text}</p>;
}
